import { ConstantItems, getDefaultConfig } from '../../constants'
import { Trade } from './models/trade';
import { TradeSet } from './models/tradeSet';
import { TradeSheet } from './models/tradeSheet';
import { TradeRestriction } from './models/tradeRestriction';
import { SecurityTradeDetail } from './models/securityTradeDetail';
import { tradeSheetGenerationRequest } from './models/tradeSheetGenerationRequest';
import { GenerateSimulationResponse } from '../simulations/models/generateSimulationResponse';
import { GenerateSimulationRequest } from '../simulations/models/generateSimulationRequest';
import { simulationTradeSheetRequest } from '../simulations/models/simulationTradeSheetRequest';
import { Locality } from '../cmodel/cmodel.repo';

export class TradingRepo {
  static $inject = ["$http", "config"];

  constructor(private $http: angular.IHttpService, private config: ConstantItems) { }

  getLatestTrades(clientId: number, portfolioId: number, displayCurrency: string) {
    let queryParams = {
      clientId,
      portfolioId,
      displayCurrency
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/trades`, config);
  }

  getRecommendations(clientId: number, portfolioId: number, displayCurrency: string = "MUR") {
    let queryParams = {
      clientId,
      portfolioId,
      displayCurrency
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/recommendations`, config);
  }

  getTradePreview(tradeSetId: number) {
    let queryParams = {
      tradeSetId: tradeSetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.tradingUrl}/trades/${tradeSetId}/orders`, config);
  }

  saveTrades(clientId: number, portfolioId: number, tradeLocality: Locality, tradeSet: TradeSet) {
    // TODO: move into simple payload vs Query and Body combo
    let queryParams = {
      clientId: clientId,
      portfolioId: portfolioId,
      locality: tradeLocality
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.post(`${this.config.tradingUrl}/trades`, tradeSet, config);

  }

  getTradeSummary(fromDate, toDate, displayAllClientTrades) {
    const queryParams = {
      from: fromDate,
      to: toDate,
      allClientTrades: displayAllClientTrades
    };
    const config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.tradingUrl}/clienttradesummaries`, config);
  }

  finaliseTrades(tradeSetId: number) {
    let queryParams = {
      tradeSetId: tradeSetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.put(`${this.config.tradingUrl}/trades/${tradeSetId}/finalise`, config);
  }

  deleteTradeSet(tradeSetId: number) {
    let queryParams = {
      tradeSetId: tradeSetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.delete(`${this.config.tradingUrl}/trades/${tradeSetId}`, config);
  }

  getTradeSheets(fromDate, toDate, displayAllSheets) {
    const queryParams = {
      from: fromDate,
      to: toDate,
      allSheets: displayAllSheets
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/tradesheets`, config);
  }

  getAdminTradeSheets(id?: number) {
    let queryParams = {

    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/admin/tradesheets/${id}`, config);
  }

  getActiveTradeDetails(securityId: number, portfolioId: number) {
    let config = getDefaultConfig();
    config.params = {
      securityId,
      portfolioId
    };
    return this.$http.get(`${this.config.tradingUrl}/trades/details`, config);
  }

  getPortfolioManagers() {
    let config = getDefaultConfig();
    return this.$http.get(`${this.config.tradingUrl}/admin/portfolios/managers`, config);
  }

  // generateTradeSheet() {
  //   let queryParams = {
  //   };
  //   let config = getDefaultConfig();
  //   config.params = queryParams;
  //   return this.$http.post(`${this.config.tradingUrl}/tradesheets/generate`, config);
  // }

  getDownloadLink(tradeSheetId: number) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/tradesheets/${tradeSheetId}/download`, config);
  }

  getDownloadLinkAsAdmin(tradeSheetId: number) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/download`, config);
  }

  submitTradeSheet(tradeSheetId: number) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.put(`${this.config.tradingUrl}/tradesheets/${tradeSheetId}/submit`, config);
  }

  sendTradeSheet(tradeSheetId: number) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.put(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/send`, config);
  }

  completeTradeSheet(tradeSheetId: number, tradeSheet: TradeSheet) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.put(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/complete`, tradeSheet, config);
  }

  cancelTradeSheetAsAdmin(tradeSheetId: number, tradeSheet: TradeSheet) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.put(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/cancel`, tradeSheet, config);
  }

  generateTradeSheetWithTradeDetails(securityTradeDetails: tradeSheetGenerationRequest) {
    let config = getDefaultConfig();
    return this.$http.post(`${this.config.tradingUrl}/tradesheets/generate`, securityTradeDetails, config)
      .then(resp => resp.data as { url: string });
  }

  getAllTradeSetSecurities(tradeSetIds: number[]) {
    let queryParams = {
      tradeSetIds: tradeSetIds
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/tradesets/securities`, config);
  }

  cancelTradeSheet(tradeSheetId: number, tradeSheet: TradeSheet) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.put(`${this.config.tradingUrl}/tradesheets/${tradeSheetId}/cancel`, tradeSheet, config);
  }

  getTradeSheetPortfolioTrades(tradeSheetId: number) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/tradesheets/${tradeSheetId}/portfolioTrades`, config);
  }

  getTradeSheetOrders(tradeSheetId: number) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/orders`, config);
  }

  sendOrderUpdates(tradeSheetId, tradeOrderUpdates: Array<any>) {
    let queryParams = {
      tradeSheetId: tradeSheetId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.post(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/orders/updates`, tradeOrderUpdates, config);
  }

  getOrderUpdates(tradeSheetId, orderId) {
    let queryParams = {
      tradeSheetId: tradeSheetId,
      orderId: orderId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/orders/${orderId}/updates`, config);
  }

  deleteOrderUpdate(tradeSheetId, orderId, updateId) {
    let queryParams = {
      tradeSheetId: tradeSheetId,
      orderId: orderId,
      updateId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.delete(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/orders/${orderId}/updates/${updateId}`, config);
  }

  getClientPortfolioTradeRestrictions(clientId) {
    let queryParams = {};
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/clients/${clientId}/restrictions`, config);
  }

  getPortfolioTradeRestrictions(portfolioId) {
    let queryParams = {
      portfolioId: portfolioId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/portfolios/${portfolioId}/restrictions`, config);
  }

  addPortfolioTradeRestriction(portfolioId, tradeRestrictionDTO) {
    let queryParams = {
      portfolioId: portfolioId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.post(`${this.config.tradingUrl}/portfolios/${portfolioId}/restrictions`, tradeRestrictionDTO, config);
  }

  updatePortfolioTradeRestriction(portfolioId, tradeRestrictionDTO: TradeRestriction) {
    let queryParams = {
      portfolioId: portfolioId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.put(`${this.config.tradingUrl}/portfolios/${portfolioId}/restrictions/${tradeRestrictionDTO.id}`, tradeRestrictionDTO, config);
  }

  
  deleteAssetClassTypeTradeRestriction(portfolioId, tradeRestrictionDTO: TradeRestriction) {
    let queryParams = {
      portfolioId: portfolioId
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.put(`${this.config.tradingUrl}/portfolios/${portfolioId}/deleteassetclassrestriction/${tradeRestrictionDTO.id}`, tradeRestrictionDTO, config);
  }

  getTradePortfolioManagers() {
    let config = getDefaultConfig();
    return this.$http.get<string[]>(`${this.config.tradingUrl}/trades/portfolios/managers`, config)
      .then(resp => resp.data)
  }

  generateSimulation(simulationRequest: GenerateSimulationRequest) {
    let config = getDefaultConfig();
    return this.$http.post<GenerateSimulationResponse>(`${this.config.tradingUrl}/simulations`, simulationRequest, config)
      .then(resp => resp.data)
  }

  saveSimulationTradeSheet(simulationTradeSheetRequest: simulationTradeSheetRequest) {
    let config = getDefaultConfig();
    return this.$http.post(`${this.config.tradingUrl}/simulations/tradesheet`, simulationTradeSheetRequest, config)
      .then(resp => resp.data as any)
  }

  getClientPortfolioForManager(manager: string) {
    let queryParams = {
      manager
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.tradingUrl}/trades/clientportfolios`, config)
      .then(resp => resp.data as any)

  }


  updateSecurityCommentForTradeSheet = (tradeSheetId, securityId, comment) => {
    let queryParams = {
      comment : comment
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(`${this.config.tradingUrl}/admin/tradesheets/${tradeSheetId}/securities/${securityId}`, null, config)

  }
}



// WEBPACK FOOTER //
// ./src/app/trading/trading.repo.ts